import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { jerseyApi, ParseResponse } from '../../api/jerseyApi';
import { useSnackbar } from '../../contexts/SnackbarContext';
import SVGPreview from './SVGPreview';
import InteractiveEditor, { InteractiveData } from './InteractiveEditor';

interface JerseyEditorProps {
  jerseyId?: number;
  onSave?: (jersey: any) => void;
  onCancel?: () => void;
  initialDescription?: string; // editable string (preview)
  initialOriginalString?: string; // immutable original string (read-only)
}

// Helper: map ParseResponse -> InteractiveData (preserve elements, colours and pattern colours faithfully)
const mapParseToInteractive = (pr: ParseResponse) => {
  console.log('[mapParseToInteractive] Input:', pr);
  const normalizeType = (name?: string) => {
    const n = (name || '').toLowerCase();
    if (n === 'sleeve') return 'sleeves';
    if (n === 'body') return 'shirt';
    return n || 'base';
  };

  const base = pr.base_color || null;

  const elements = (pr.elements || []).map((e: any, idx: number) => {
    console.log(`[mapParseToInteractive] Processing element ${idx}:`, e);

    // Extract pattern names as free-text labels used by the UI, deduped and trimmed
    const patternNames: string[] = Array.isArray(e.patterns)
      ? Array.from(new Set(
          e.patterns
            .map((p: any) => (typeof p === 'string' ? p : (p.pattern_name || p.pattern_slug || p.description || '')))
            .map((s: any) => String(s).trim())
            .filter(Boolean)
        ))
      : [];

    console.log(`[mapParseToInteractive] Element ${idx} pattern names:`, patternNames);
    console.log(`[mapParseToInteractive] Element ${idx} pattern_colors from backend:`, e.pattern_colors);

    // Build a stable pattern_colors mapping keyed by the same labels in patternNames
    const patternColors: Record<string, string[]> = {};
    if (patternNames.length > 0) {
      const src = (e.pattern_colors && typeof e.pattern_colors === 'object') ? (e.pattern_colors as Record<string, any>) : {};
      for (const name of patternNames) {
        // Try exact key first, then case-insensitive match only; no unrelated fallback
        let list: any = src[name];
        if (!Array.isArray(list)) {
          const foundKey = Object.keys(src).find(k => k.toLowerCase() === String(name).toLowerCase());
          if (foundKey && Array.isArray(src[foundKey])) list = src[foundKey];
        }
        patternColors[name] = Array.isArray(list) ? list.map(String) : [];
      }
    }

    console.log(`[mapParseToInteractive] Element ${idx} mapped pattern_colors:`, patternColors);

    // Preserve the parser's element colour as-is; do not swap with pattern colours
    const outColor: string | null = e.color || null;
    console.log(`[mapParseToInteractive] Element ${idx} color:`, outColor);

    // Preserve additional parser metadata if present
    const extra: any = {};
    ['explicit_color', 'visibility_adjusted', 'raw_text'].forEach((k) => {
      if (k in e) extra[k] = e[k];
    });

    return {
      element_type: normalizeType(e.element_type),
      color: outColor,
      patterns: patternNames,
      pattern_colors: patternColors,
      order: typeof e.order === 'number' ? e.order : idx,
      ...extra,
    } as any;
  });

  return { base_color: base, elements } as InteractiveData;
};

const JerseyEditor: React.FC<JerseyEditorProps> = ({
  jerseyId,
  onSave,
  onCancel,
  initialDescription,
  initialOriginalString,
}) => {
  const [formData, setFormData] = useState({
    description: initialDescription || initialOriginalString || '', // editable string (preview or fallback to original)
  });
  const [originalString, setOriginalString] = useState<string>(initialOriginalString || initialDescription || '');
  const [parseResult, setParseResult] = useState<ParseResponse | null>(null);
  const [isParsingLive, setIsParsingLive] = useState(false);

  // Determine UI mode for main action button/header
  // Edit mode when: editing an existing jersey (jerseyId) OR editing an approved silk (initialOriginalString present)
  const isApprovedEdit = !!(initialOriginalString && initialOriginalString.trim().length > 0);
  const isEditMode = !!(jerseyId || isApprovedEdit);

  // Mode: text or interactive
  const [mode, setMode] = useState<'text' | 'interactive'>('text');
  const [interactive, setInteractive] = useState<InteractiveData>({ base_color: null, elements: [] as any });
  const [currentSvgs, setCurrentSvgs] = useState<{ front: string; side: string } | null>(null);

  // Memoize the SVG ready callback to prevent infinite re-renders
  const handleSvgsReady = useCallback((front: string, side: string) => {
    setCurrentSvgs({ front, side });
  }, []);

  // On mount: for edit flow only (when initialOriginalString is provided), seed original
  useEffect(() => {
    if (!initialOriginalString) return; // URL prefill handled in the next effect
    setOriginalString(initialOriginalString);
    if (!formData.description) {
      setFormData((prev) => ({ ...prev, description: initialDescription || initialOriginalString || '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // URL prefill consistency: if opened with only initialDescription (no initialOriginalString),
  // look up an existing ApprovedColourScheme and, if it has a preview_string, use that for the editable field.
  const prefillAppliedRef = useRef(false);
  console.debug('initialDescription, initialOriginalString');
  console.debug(initialDescription, initialOriginalString);
  useEffect(() => {
    // Run only for URL prefill scenario and only once per distinct prefill value
    if (initialOriginalString || !initialDescription || prefillAppliedRef.current) return;
    const prefill = initialDescription;
    setOriginalString(prefill); // Original (read-only) is always the exact URL prefill
    let cancelled = false;
    (async () => {
      try {
        // Debug: trace lookup
        console.log('[JerseyEditor] Lookup existing scheme for prefill:', prefill);
        const rec = await jerseyApi.lookupApprovedSchemeByColours(prefill);
        const previewRaw: any = rec?.preview_string;
        const preview: string = typeof previewRaw === 'string' ? previewRaw.trim() : '';
        console.log('[JerseyEditor] Lookup result:', rec);
        console.log('[JerseyEditor] preview_string check => raw:', previewRaw, 'trimmed:', preview, 'length:', preview.length, 'cancelled:', cancelled);
        if (!cancelled && preview.length > 0) {
          console.log('[JerseyEditor] Applying preview_string to editable field:', preview);
          setFormData((prev) => ({ ...prev, description: preview }));
          prefillAppliedRef.current = true;
        } else if (!cancelled) {
          console.log('[JerseyEditor] No preview_string found; falling back to prefill for editable');
          setFormData((prev) => ({ ...prev, description: prefill }));
          prefillAppliedRef.current = true;
        } else {
          console.log('[JerseyEditor] Request was cancelled; will allow a subsequent run to handle prefill');
        }
      } catch (err) {
        console.warn('[JerseyEditor] Lookup failed or not found; using prefill as editable. Error:', err);
        setFormData((prev) => ({ ...prev, description: prefill }));
        prefillAppliedRef.current = true;
      }
    })();
    return () => { cancelled = true; };
  }, [initialDescription, initialOriginalString]);


  // When switching to Interactive mode, seed from current parseResult
  useEffect(() => {
    if (mode !== 'interactive') return;
    if (!parseResult || !parseResult.elements?.length) return;
    console.log('[JerseyEditor] Mapping parseResult to interactive:', parseResult);
    const mapped = mapParseToInteractive(parseResult);
    setInteractive(mapped);
  }, [mode, parseResult]);

  // Use ref to track the parse timeout
  const parseTimeoutRef = useRef<NodeJS.Timeout>();
  const lastParsedDescription = useRef<string>('');

  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  // Fetch jersey data if editing
  const { data: jersey, isLoading: jerseyLoading } = useQuery({
    queryKey: ['jersey', jerseyId],
    queryFn: () => jerseyApi.getJersey(jerseyId!),
    enabled: !!jerseyId,
  });

const parseMutation = useMutation({
  mutationFn: async (description: string) => {
    console.log('📤 Sending parse request:', { description });
    const response = await jerseyApi.parseDescription({ description });
    console.log('📥 Parse response:', response);
    return response;
  },
  onSuccess: (data) => {
    console.log('✅ Parse successful:', data);
    setParseResult(data);
    setIsParsingLive(false);
  },
  onError: (error) => {
    console.error('❌ Parse failed:', error);
    setIsParsingLive(false);
    showSnackbar('Failed to parse description', 'error');
  },
});
  // Save jersey mutation
  const saveMutation = useMutation({
    mutationFn: (data: any) => jerseyApi.saveJersey(data),
    onSuccess: (data) => {
      showSnackbar('Jersey saved successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['jerseys'] });
      onSave?.(data);
    },
    onError: (error: any) => {
      showSnackbar(
        error.response?.data?.detail || 'Failed to save jersey',
        'error'
      );
    },
  });

  // Load jersey data when editing
  useEffect(() => {
    if (jersey) {
      setFormData({
        description: jersey.description || '',
      });
      // Set the last parsed description to avoid re-parsing on load
      lastParsedDescription.current = jersey.description || '';
    }
  }, [jersey]);

  // Live parsing with debounce (disabled while in Interactive mode)
  useEffect(() => {
    // Do not live-parse while in Interactive mode; preview derives from interactive state
    if (mode === 'interactive') {
      if (parseTimeoutRef.current) {
        clearTimeout(parseTimeoutRef.current);
      }
      setIsParsingLive(false);
      return;
    }

    // Clear any existing timeout
    if (parseTimeoutRef.current) {
      clearTimeout(parseTimeoutRef.current);
    }

    const currentDescription = formData.description.trim();

    // Check if description is long enough and different from last parsed
    if (
      currentDescription &&
      currentDescription.length > 3 &&
      currentDescription !== lastParsedDescription.current
    ) {
      parseTimeoutRef.current = setTimeout(() => {
        // Only parse if not already parsing
        if (!parseMutation.isPending && !isParsingLive) {
          setIsParsingLive(true);
          lastParsedDescription.current = currentDescription;
          parseMutation.mutate(currentDescription);
        }
      }, 1000);
    } else if (!currentDescription || currentDescription.length <= 3) {
      // Clear parse result if description is too short
      setParseResult(null);
      setIsParsingLive(false);
      lastParsedDescription.current = '';
    }

    // Cleanup
    return () => {
      if (parseTimeoutRef.current) {
        clearTimeout(parseTimeoutRef.current);
      }
    };
  }, [mode, formData.description, parseMutation.isPending, isParsingLive, parseMutation]);

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const description = formData.description.trim();
      if (!description) return;

      // Decide source of truth for preview/save
      // - Interactive mode: use current interactive state (as shown in preview)
      // - Text mode: use parsed result if available, otherwise parse now
      let elements: any[] = [];
      let baseColor: string | null | undefined = null;

      if (mode === 'interactive' && interactive?.elements?.length) {
        elements = interactive.elements as any[];
        baseColor = interactive.base_color;
      } else if (parseResult?.elements?.length) {
        elements = parseResult.elements as any[];
        baseColor = parseResult.base_color;
      } else {
        const parsed = await jerseyApi.parseDescription({ description });
        elements = parsed.elements as any[];
        baseColor = parsed.base_color;
      }

      // Use current SVGs if available (they may have been modified by pattern transform fixes)
      // Otherwise generate new ones
      let frontSvg, sideSvg;
      if (currentSvgs) {
        frontSvg = currentSvgs.front;
        sideSvg = currentSvgs.side;
        console.log('Using current SVGs (potentially modified by pattern fixes)');
      } else {
        [frontSvg, sideSvg] = await Promise.all([
          jerseyApi.generateSVGPreview({ elements, base_color: baseColor || null, view: 'front' }),
          jerseyApi.generateSVGPreview({ elements, base_color: baseColor || null, view: 'side' }),
        ]);
        console.log('Generated new SVGs');
      }

      // Save as approved silk and write to saved_previews
      await jerseyApi.savePreview({
        id: 0,
        name: 'Jersey',
        tab_no: 0,
        colours: description,
        front_svg: frontSvg,
        side_svg: sideSvg,
        preview_string: description,
        original_string: originalString || description,
      });

      showSnackbar('Jersey created and saved to Approved Silks', 'success');
      onSave?.({ description });
    } catch (error: any) {
      showSnackbar(error?.response?.data?.detail || 'Failed to create jersey', 'error');
    }
  };

  const isLoading = jerseyLoading || saveMutation.isPending;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Grid container spacing={3} sx={{ height: 'calc(100vh - 200px)', alignItems: 'stretch' }}>
        {/* Form Section - Scrollable */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', maxHeight: '100%' }}>
          <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
              <Typography variant="h6" gutterBottom>
                {isEditMode ? 'Edit Jersey' : 'Create New Jersey'}
              </Typography>

              {/* Mode toggle */}
              <ToggleButtonGroup
                color="primary"
                exclusive
                size="small"
                value={mode}
                onChange={(_, val) => {
                  if (!val) return;
                  if (val === 'interactive' && parseResult?.elements?.length) {
                    setInteractive(mapParseToInteractive(parseResult));
                  }
                  setMode(val);
                }}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="text">Text</ToggleButton>
                <ToggleButton value="interactive">Interactive</ToggleButton>
              </ToggleButtonGroup>

              <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>

                {mode === 'text' ? (
                  <>
                    <TextField
                      fullWidth
                      label="Original Colour String (read-only)"
                      value={originalString}
                      margin="normal"
                      multiline
                      rows={2}
                      InputProps={{ readOnly: true }}
                      helperText="Immutable original string from URL prefill"
                    />

                    <TextField
                      fullWidth
                      label="Editable Colour String"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      margin="normal"
                      multiline
                      rows={3}
                      required
                      helperText="Describe the jersey colors and patterns (e.g., 'red with blue striped sleeves')"
                    />

                    <Box sx={{ mt: 1 }}>
                      {isParsingLive && (
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          <Typography variant="body2">Parsing description...</Typography>
                        </Box>
                      )}
                    </Box>
                  </>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      label="Original Colour String (read-only)"
                      value={originalString}
                      margin="normal"
                      multiline
                      rows={2}
                      InputProps={{ readOnly: true }}
                      helperText="Immutable original string from URL prefill"
                    />

                    <TextField
                      fullWidth
                      label="Editable Colour String"
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      margin="normal"
                      multiline
                      rows={3}
                      required
                      helperText="Describe the jersey colors and patterns (e.g., 'red with blue striped sleeves')"
                    />

                    <InteractiveEditor
                      value={interactive}
                      onChange={(val) => {
                        setInteractive(val);
                        // Convert to ParseResponse-compatible shape for preview
                        setParseResult({
                          base_color: val.base_color,
                          elements: val.elements as any,
                          confidence_score: 1,
                          errors: [],
                        });
                        // Also update the editable colour string to a canonical, parser-friendly form
                        const segs: string[] = [];

                        const norm = (s?: string | null) => (s || '').trim();
                        const lc = (s?: string | null) => norm(s).toLowerCase();

                        const elements = (val.elements || []).filter(e => lc(e.element_type) !== 'base');
                        const findEl = (t: string) => elements.find(e => lc(e.element_type) === t);
                        const patName = (e: any) => (e?.patterns && e.patterns.length > 0 ? String(e.patterns[0]) : '');
                        const patCols = (e: any, p: string) => (p && e?.pattern_colors && e.pattern_colors[p]) ? e.pattern_colors[p] : [];
                        const sameList = (a: string[] = [], b: string[] = []) => a.length === b.length && a.every((v, i) => v === b[i]);

                        // Check if a pattern is a replacement pattern (needs element color + pattern colors)
                        const isReplacementPattern = (p: string) => {
                          const pl = lc(p);
                          return pl.includes('halve') || pl.includes('half') ||
                                 pl.includes('quarter') || pl.includes('third') ||
                                 pl.includes('diagonal');
                        };

                        const phrasePattern = (element: any, p: string, cols: string[], label?: string) => {
                          // For replacement patterns, include element color + pattern colors
                          if (isReplacementPattern(p) && element?.color && lc(element.color) !== lc(val.base_color)) {
                            const allColors = [element.color, ...(cols || [])];
                            const head = `${allColors.join(' and ')} ${p}`;
                            return label ? `${head} ${label}` : head;
                          }
                          // For overlay patterns, only use pattern colors
                          const head = (cols && cols.length > 0) ? `${cols.join(' and ')} ${p}` : `${p}`;
                          return label ? `${head} ${label}` : head;
                        };
                        const phraseColor = (color: string, label: string) => `${color} ${label}`;

                        // 1) Base colour first
                        if (val.base_color) segs.push(String(val.base_color));

                        // 2) Shirt
                        const shirt = findEl('shirt');
                        if (shirt) {
                          const p = patName(shirt);
                          const pcs = patCols(shirt, p);
                          if (p) {
                            // Emit pattern with proper color handling (default target is shirt)
                            segs.push(phrasePattern(shirt, p, pcs));
                          } else if (shirt.color && lc(shirt.color) !== lc(val.base_color)) {
                            segs.push(phraseColor(String(shirt.color), 'shirt'));
                          }
                        }

                        // 3) Sleeves + Cap (combine when identical)
                        const sleeves = findEl('sleeves');
                        const cap = findEl('cap');
                        const sp = patName(sleeves);
                        const sc = sleeves ? patCols(sleeves, sp) : [];
                        const cp = patName(cap);
                        const cc = cap ? patCols(cap, cp) : [];

                        if (sp && cp && sp === cp && sameList(sc, cc)) {
                          segs.push(phrasePattern(sleeves, sp, sc, 'sleeves and cap'));
                        } else {
                          if (sleeves) {
                            if (sp) segs.push(phrasePattern(sleeves, sp, sc, 'sleeves'));
                            else if (sleeves.color) segs.push(phraseColor(String(sleeves.color), 'sleeves'));
                          }
                          if (cap) {
                            if (cp) segs.push(phrasePattern(cap, cp, cc, 'cap'));
                            else if (cap.color) segs.push(phraseColor(String(cap.color), 'cap'));
                          }
                        }

                        // 4) Other elements (e.g., cuffs, armbands, collar)
                        const handled = new Set(['shirt', 'sleeves', 'cap']);
                        elements.forEach((el) => {
                          const t = lc(el.element_type);
                          if (handled.has(t)) return;
                          const p = patName(el);
                          const pcs = patCols(el, p);
                          if (p) segs.push(phrasePattern(el, p, pcs, el.element_type));
                          else if (el.color) segs.push(phraseColor(String(el.color), el.element_type));
                        });

                        const text = segs.filter(Boolean).join(', ');
                        setFormData(prev => ({ ...prev, description: text }));
                      }}
                    />
                  </>
                )}

                {/* Simplified: No Race, Trainer, or Number fields required */}

                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={isLoading}
                    startIcon={isLoading && <CircularProgress size={20} />}
                  >
                    {isEditMode ? 'Update' : 'Create'} Jersey
                  </Button>
                  {onCancel && (
                    <Button variant="outlined" onClick={onCancel}>
                      Cancel
                    </Button>
                  )}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Preview Section - Fixed, Always Visible */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', maxHeight: '100%' }}>
          <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Typography variant="h6" gutterBottom sx={{ flexShrink: 0 }}>
                Live Preview
              </Typography>

              {/* Scrollable preview content */}
              <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                {/* Parsing Status */}
                {isParsingLive && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography variant="body2">Parsing description...</Typography>
                  </Box>
                )}

                {/* Parse Results */}
                {parseResult && !isParsingLive && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Parsed Elements:
                    </Typography>

                    {parseResult.base_color && (
                      <Chip
                        label={`Base: ${parseResult.base_color}`}
                        color="primary"
                        size="small"
                        sx={{ mr: 1, mb: 1 }}
                      />
                    )}

                    {parseResult.elements.map((element, index) => {
                      // Handle pattern display - patterns can be strings or objects
                      const patternDisplay = element.patterns?.length > 0
                        ? element.patterns.map((pattern: any) =>
                            typeof pattern === 'string' ? pattern : pattern.pattern_name || pattern.pattern_slug || 'unknown'
                          ).join(', ')
                        : '';

                      return (
                        <Chip
                          key={index}
                          label={`${element.element_type}: ${element.color || 'no color'} ${
                            patternDisplay ? `(${patternDisplay})` : ''
                          }`}
                          variant="outlined"
                          size="small"
                          sx={{ mr: 1, mb: 1 }}
                        />
                      );
                    })}

                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Confidence: {(parseResult.confidence_score * 100).toFixed(1)}%
                    </Typography>

                    {parseResult.errors.length > 0 && (
                      <Alert severity="warning" sx={{ mt: 1 }}>
                        {parseResult.errors.join(', ')}
                      </Alert>
                    )}
                  </Box>
                )}

                {/* SVG Preview */}
                {parseResult && parseResult.elements?.length > 0 ? (
                  <SVGPreview
                    description={formData.description}
                    parseResult={parseResult}
                    onSvgsReady={handleSvgsReady}
                  />
                ) : (
                  <Box
                    sx={{
                      height: 300,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {mode === 'text' ? 'Enter a description to see live preview' : 'Use the Interactive editor to build a jersey'}
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default JerseyEditor;